import { SUGGESTED_PRIMARY_REG } from "./career";
import {
  classifyTankLoad,
  engineFlowPph,
  resolveMeasuredBurn,
  shutdownGate,
  type BurnReason,
  type FuelShutdownSnapshot,
  type FuelTankLb,
  type TankLoadKind,
} from "./fuelBurn";
import { convertFuel, fuelUsedToUsGal, isFuelUnit, JET_A_LB_PER_US_GAL, type FuelUnit } from "./fuelUnits";
import { hydrateFlightPhaseId, phaseIsAirborne, type FlightPhaseId } from "./flightPhase";
import { TELEMETRY_STORE_MIN_MS } from "./mapMemory";
import { SIM_DRAFT_AIR_GAP_MS, SIM_DRAFT_GAP_MS, SIM_DRAFT_TAXI_GAP_MS, SIM_TAXI_KT } from "./simDraft";
import type { AircraftSpec, FlightLogEntry, Mission } from "./types";

export function careerBeforeFromTail(spec: AircraftSpec): TailCareerBefore {
  return {
    fuelOnBoardGal: spec.fuelOnBoardGal,
    airframeHours: spec.airframeHours,
    engineHours: spec.engineHours,
    apuHours: spec.apuHours,
    engineHoursEach: spec.engineHoursEach ? [...spec.engineHoursEach] : undefined,
    cycles: spec.cycles,
    lastAirportIcao: spec.lastAirportIcao,
    status: spec.status,
    available: spec.available,
  };
}

/**
 * Per-registration SimConnect telemetry.
 *
 * Persist only reliable vars: kinematics (already on the desk), fuel quantity,
 * engine combustion / N1, APU RPM when the sim publishes it. Never oil or
 * hydraulic quantity — those are often fake on GA / bizjet add-ons.
 */

export const TAIL_TRIP_TELEMETRY_MAX = 200;
export const N1_RUNNING_PCT = 12;
export const APU_RUNNING_PCT = 50;
/** Hawker usable is 10 000 lb; allow a margin for other types later. */
export const FUEL_QTY_LB_MAX = 25_000;
export const TELEMETRY_MIN_DT_MS = 400;
export { TELEMETRY_STORE_MIN_MS };

export type EngineTimeSource = "combustion" | "n1" | "phase_fallback";

export type SimTelemetrySample = {
  atUtc: string;
  registration: string;
  aircraftId?: string;
  tripId?: string;
  flightLogId?: string;
  callsign?: string;
  originIcao?: string;
  destIcao?: string;
  phase: FlightPhaseId;
  onGround: boolean | null;
  groundSpeed: number | null;
  airspeed: number | null;
  /** FUEL TOTAL QUANTITY WEIGHT — pounds. */
  fuelQtyLb: number | null;
  eng1Combustion: boolean | null;
  eng2Combustion: boolean | null;
  eng1N1: number | null;
  eng2N1: number | null;
  /** APU PCT RPM. Null = var missing / unreliable. */
  apuPctRpm: number | null;
  eng3Combustion?: boolean | null;
  eng4Combustion?: boolean | null;
  /** ENG FUEL FLOW PPH, pounds per hour, as requested by the SimConnect writer. */
  eng1FlowPph?: number | null;
  eng2FlowPph?: number | null;
  eng3FlowPph?: number | null;
  eng4FlowPph?: number | null;
  unlimitedFuel?: boolean | null;
  fuelDumpActive?: boolean | null;
  tankCenterLb?: number | null;
  tankLeftMainLb?: number | null;
  tankRightMainLb?: number | null;
  tankLeftAuxLb?: number | null;
  tankRightAuxLb?: number | null;
  source: "simconnect";
  /** 1–4 from the tail. Absent samples keep the session count, or 2. */
  engineCount?: number;
};

export type FuelTrack = {
  flowLb: number;
  flowSeen: boolean;
  unlimited: boolean;
  dump: boolean;
  engSeen: [boolean, boolean, boolean, boolean];
  apuEverPublished: boolean;
  sawPower: boolean;
  shutdownLatched: boolean;
  tankLoad?: TankLoadKind;
};

/** Tail figures captured when the sim session opens, before this flight posts. */
export type TailCareerBefore = {
  fuelOnBoardGal?: number;
  airframeHours: number;
  engineHours?: number;
  apuHours?: number;
  engineHoursEach?: number[];
  cycles?: number;
  lastAirportIcao?: string;
  status: "available" | "grounded" | "ferry" | "in_mx";
  available: boolean;
};

export function emptyFuelTrack(): FuelTrack {
  return {
    flowLb: 0,
    flowSeen: false,
    unlimited: false,
    dump: false,
    engSeen: [false, false, false, false],
    apuEverPublished: false,
    sawPower: false,
    shutdownLatched: false,
  };
}

export type TailTelemetrySession = {
  registration: string;
  aircraftId?: string;
  tripId?: string;
  flightLogId?: string;
  callsign?: string;
  originIcao?: string;
  destIcao?: string;
  startedAtUtc: string;
  lastAtUtc: string;
  airTimeMs: number;
  blockMs: number;
  engineMs: number;
  apuMs: number;
  fuelStartLb?: number;
  fuelLastLb?: number;
  /** Sum of quantity decreases (handles a mid-session uplift). */
  fuelBurnedLb: number;
  engineSource: EngineTimeSource;
  apuReliable: boolean;
  airborneSeen: boolean;
  offBlocksUtc?: string;
  airborneUtc?: string;
  landedUtc?: string;
  onBlocksUtc?: string;
  /** Tanks at wheels-off and first touchdown: the span SimBrief trip fuel covers. */
  fuelAirborneLb?: number;
  fuelLandedLb?: number;
  /** Engine fuel flow integrated while airborne only. */
  airFlowLb?: number;
  closed?: boolean;
  lastAirborne?: boolean;
  lastEngineRunning?: boolean;
  lastApuRunning?: boolean;
  fuelTrack?: FuelTrack;
  /** Combustion milliseconds per engine index 0–3. */
  engineMsEach?: number[];
  lastEngineOn?: boolean[];
  engineCount?: number;
  /** GENERAL ENG COMBUSTION true on an installed engine. N1 and the APU do not set this. */
  combustionTrueSeen?: boolean;
  careerBefore?: TailCareerBefore;
};

export type TailTelemetryTotals = {
  registration: string;
  aircraftId?: string;
  airTimeHours: number;
  blockHours: number;
  engineHours: number;
  apuHours: number;
  lastAtUtc?: string;
  engineSource: EngineTimeSource;
  apuReliable: boolean;
  tripCount: number;
};

export type TailTripTelemetry = {
  id: string;
  registration: string;
  aircraftId?: string;
  tripId?: string;
  flightLogId?: string;
  callsign?: string;
  originIcao?: string;
  destIcao?: string;
  startedAtUtc: string;
  endedAtUtc: string;
  airTimeHours: number;
  blockHours: number;
  engineHours: number;
  apuHours: number | null;
  fuelStartAmount?: number;
  fuelEndAmount?: number;
  fuelUsedAmount?: number;
  fuelUnit: FuelUnit;
  engineSource: EngineTimeSource;
  apuReliable: boolean;
  source: "simconnect";
  /** First out / wheels-off / touchdown / on-blocks from the closed session. */
  offBlocksUtc?: string;
  airborneUtc?: string;
  landedUtc?: string;
  onBlocksUtc?: string;
  /** False when unlimited fuel, a dump, or a failed flow cross-check blocked the burn. */
  fuelBurnReliable?: boolean;
  fuelBurnReason?: BurnReason | null;
  /** Maintenance runtime: max per-engine tenth, else the union clock rounded up to 0.1 h. */
  engineMxHours?: number;
  /** Per-engine tenths, length = engine count. */
  engineHoursEach?: number[];
  /** Exact run as m:ss, one figure per engine. The ledger stays in tenths. */
  engineDetail?: string;
  careerBefore?: TailCareerBefore;
};

export type TailTelemetryState = {
  session: TailTelemetrySession | null;
  totals: Record<string, TailTelemetryTotals>;
  trips: TailTripTelemetry[];
  /** Tanks at the last engines-and-APU shutdown. Older saves omit this. */
  shutdownByTail?: Record<string, FuelShutdownSnapshot>;
};

export const EMPTY_TAIL_TELEMETRY: TailTelemetryState = {
  session: null,
  totals: {},
  trips: [],
  shutdownByTail: {},
};

export const TELEMETRY_RELIABILITY_GAPS = [
  "Oil quantity — not read. Add-on oil gauges are often decorative.",
  "Hydraulic quantity — not read. Same reason.",
  "APU on-time only when APU PCT RPM is published and moves; otherwise left blank.",
  "Engine time prefers GENERAL ENG COMBUSTION; TURB ENG N1 is the fallback; taxi+air phase is last resort.",
  "Fuel burn is the tank decrease, cross-checked with engine fuel flow. Unlimited fuel or an active dump leaves the burn unrecorded. Increases are not counted as negative burn.",
] as const;

function asNum(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function asBool(value: unknown): boolean | null {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  if (value === false || value === 0 || value === "0" || value === "false") return false;
  return null;
}

function sampleTime(atUtc: string): number {
  const t = new Date(atUtc).getTime();
  return Number.isFinite(t) ? t : Number.NaN;
}

export function hoursFromMs(ms: number): number {
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.round((ms / 3_600_000) * 100) / 100;
}

/** One maintenance tenth. Any partial tenth rounds up. Zero stays zero. */
export const MX_TENTH_MS = 360_000;
/** Desk reconnect may backfill at most this much of a missed airborne window. */
export const DESK_GAP_BACKFILL_MS = 18 * 3_600_000;

export function mxTenthsFromMs(ms: number): number {
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / MX_TENTH_MS) / 10;
}

export function formatRunMmSs(ms: number): string {
  const total = Math.max(0, Math.round((Number.isFinite(ms) ? ms : 0) / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function clampEngineCount(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 2;
  const count = Math.floor(n);
  if (count < 1) return 1;
  if (count > 4) return 4;
  return count;
}

export function engineMxFromSession(session: Pick<TailTelemetrySession, "engineMs" | "engineMsEach" | "engineCount">): {
  engineMxHours: number;
  engineHoursEach: number[];
  engineDetail: string;
} {
  const count = clampEngineCount(session.engineCount ?? 2);
  const eachMs = session.engineMsEach ?? [];
  const tracked = eachMs.slice(0, count).some((ms) => ms > 0);
  const each = Array.from({ length: count }, (_, index) => mxTenthsFromMs(eachMs[index] ?? 0));
  const engineMxHours = tracked ? Math.max(...each) : mxTenthsFromMs(session.engineMs);
  const engineDetail = Array.from({ length: count }, (_, index) => {
    return `Eng ${index + 1} ${formatRunMmSs(eachMs[index] ?? (tracked ? 0 : session.engineMs))}`;
  }).join(" · ");
  return { engineMxHours, engineHoursEach: each, engineDetail };
}

export function normalizeTailKey(value?: string | null): string {
  return (value ?? "").replace(/[\s-]/g, "").toUpperCase();
}

export function displayRegistration(value?: string | null): string {
  const raw = (value ?? "").trim().toUpperCase();
  return raw || "";
}

export function reliableFuelQtyLb(value: unknown): number | null {
  const n = asNum(value);
  if (n == null || n < 0 || n > FUEL_QTY_LB_MAX) return null;
  const whole = Math.round(n);
  if (whole < 0 || whole > FUEL_QTY_LB_MAX) return null;
  return whole;
}

export function engineRunning(sample: Pick<
  SimTelemetrySample,
  | "eng1Combustion"
  | "eng2Combustion"
  | "eng3Combustion"
  | "eng4Combustion"
  | "eng1N1"
  | "eng2N1"
  | "onGround"
  | "phase"
  | "groundSpeed"
  | "airspeed"
>): { running: boolean; source: EngineTimeSource } {
  const comb = [sample.eng1Combustion, sample.eng2Combustion, sample.eng3Combustion, sample.eng4Combustion];
  if (comb.some((v) => v === true)) return { running: true, source: "combustion" };
  if (comb.every((v) => v === false)) return { running: false, source: "combustion" };
  const known = comb.filter((v) => v != null);
  if (known.length && known.every((v) => v === false)) return { running: false, source: "combustion" };

  if (sample.eng1N1 != null || sample.eng2N1 != null) {
    const n1 = Math.max(sample.eng1N1 ?? 0, sample.eng2N1 ?? 0);
    return { running: n1 >= N1_RUNNING_PCT, source: "n1" };
  }

  return { running: sampleIsMoving(sample), source: "phase_fallback" };
}

export function apuRunning(sample: Pick<SimTelemetrySample, "apuPctRpm">): { running: boolean; reliable: boolean } {
  if (sample.apuPctRpm == null || !Number.isFinite(sample.apuPctRpm)) {
    return { running: false, reliable: false };
  }
  return { running: sample.apuPctRpm >= APU_RUNNING_PCT, reliable: true };
}

function speedKt(sample: Pick<SimTelemetrySample, "groundSpeed" | "airspeed">): number {
  const gs = asNum(sample.groundSpeed);
  if (gs != null) return Math.abs(gs);
  const ias = asNum(sample.airspeed);
  return ias != null ? Math.abs(ias) : 0;
}

export function sampleIsAirborne(sample: Pick<SimTelemetrySample, "onGround" | "phase">): boolean {
  if (sample.onGround === false) return true;
  if (sample.onGround === true) return false;
  return phaseIsAirborne(sample.phase);
}

export function sampleIsMoving(
  sample: Pick<SimTelemetrySample, "onGround" | "phase" | "groundSpeed" | "airspeed">,
): boolean {
  if (sampleIsAirborne(sample)) return true;
  if (sample.phase === "taxi" || sample.phase === "takeoff") return true;
  return speedKt(sample) >= SIM_TAXI_KT;
}

export function parseTelemetryFromPosition(raw: Record<string, unknown>): Pick<
  SimTelemetrySample,
  | "fuelQtyLb"
  | "eng1Combustion"
  | "eng2Combustion"
  | "eng3Combustion"
  | "eng4Combustion"
  | "eng1N1"
  | "eng2N1"
  | "apuPctRpm"
  | "eng1FlowPph"
  | "eng2FlowPph"
  | "eng3FlowPph"
  | "eng4FlowPph"
  | "unlimitedFuel"
  | "fuelDumpActive"
  | "tankCenterLb"
  | "tankLeftMainLb"
  | "tankRightMainLb"
  | "tankLeftAuxLb"
  | "tankRightAuxLb"
> {
  const fuelLb = reliableFuelQtyLb(
    raw.fuelQtyLb ?? raw.fuel_qty_lb ?? raw.fuelTotalQuantityWeight ?? raw.fuel_total_quantity_weight,
  );
  const fuelGal = asNum(raw.fuelQtyGal ?? raw.fuel_qty_gal ?? raw.fuelTotalQuantity ?? raw.fuel_total_quantity);
  const fromGal =
    fuelLb == null && fuelGal != null ? reliableFuelQtyLb(fuelGal * JET_A_LB_PER_US_GAL) : null;
  return {
    fuelQtyLb: fuelLb ?? fromGal,
    eng1Combustion: asBool(raw.eng1Combustion ?? raw.eng1_combustion ?? raw.generalEngCombustion1),
    eng2Combustion: asBool(raw.eng2Combustion ?? raw.eng2_combustion ?? raw.generalEngCombustion2),
    eng1N1: asNum(raw.eng1N1 ?? raw.eng1_n1 ?? raw.turbEngN1_1),
    eng2N1: asNum(raw.eng2N1 ?? raw.eng2_n1 ?? raw.turbEngN1_2),
    apuPctRpm: asNum(raw.apuPctRpm ?? raw.apu_pct_rpm ?? raw.apuRpm),
    eng3Combustion: asBool(raw.eng3Combustion ?? raw.eng3_combustion ?? raw.generalEngCombustion3),
    eng4Combustion: asBool(raw.eng4Combustion ?? raw.eng4_combustion ?? raw.generalEngCombustion4),
    eng1FlowPph: asNum(raw.eng1FlowPph ?? raw.eng1_flow_pph),
    eng2FlowPph: asNum(raw.eng2FlowPph ?? raw.eng2_flow_pph),
    eng3FlowPph: asNum(raw.eng3FlowPph ?? raw.eng3_flow_pph),
    eng4FlowPph: asNum(raw.eng4FlowPph ?? raw.eng4_flow_pph),
    unlimitedFuel: asBool(raw.unlimitedFuel ?? raw.unlimited_fuel),
    fuelDumpActive: asBool(raw.fuelDumpActive ?? raw.fuel_dump_active),
    tankCenterLb: reliableFuelQtyLb(raw.tankCenterLb ?? raw.tank_center_lb),
    tankLeftMainLb: reliableFuelQtyLb(raw.tankLeftMainLb ?? raw.tank_left_main_lb),
    tankRightMainLb: reliableFuelQtyLb(raw.tankRightMainLb ?? raw.tank_right_main_lb),
    tankLeftAuxLb: reliableFuelQtyLb(raw.tankLeftAuxLb ?? raw.tank_left_aux_lb),
    tankRightAuxLb: reliableFuelQtyLb(raw.tankRightAuxLb ?? raw.tank_right_aux_lb),
  };
}

export function hydrateSimTelemetrySample(raw?: unknown): SimTelemetrySample | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const atUtc = typeof row.atUtc === "string" ? row.atUtc : "";
  if (!atUtc || !Number.isFinite(sampleTime(atUtc))) return null;
  const phase = hydrateFlightPhaseId(row.phase);
  if (!phase) return null;
  if (row.source !== "simconnect") return null;
  const registration = displayRegistration(typeof row.registration === "string" ? row.registration : "");
  if (!registration) return null;
  const extra = parseTelemetryFromPosition(row);
  return {
    atUtc,
    registration,
    aircraftId: typeof row.aircraftId === "string" ? row.aircraftId : undefined,
    tripId: typeof row.tripId === "string" ? row.tripId : undefined,
    flightLogId: typeof row.flightLogId === "string" ? row.flightLogId : undefined,
    callsign: typeof row.callsign === "string" ? row.callsign : undefined,
    originIcao: typeof row.originIcao === "string" ? row.originIcao : undefined,
    destIcao: typeof row.destIcao === "string" ? row.destIcao : undefined,
    phase,
    onGround: asBool(row.onGround),
    groundSpeed: asNum(row.groundSpeed),
    airspeed: asNum(row.airspeed),
    fuelQtyLb: extra.fuelQtyLb,
    eng1Combustion: extra.eng1Combustion,
    eng2Combustion: extra.eng2Combustion,
    eng1N1: extra.eng1N1,
    eng2N1: extra.eng2N1,
    apuPctRpm: extra.apuPctRpm,
    eng3Combustion: extra.eng3Combustion,
    eng4Combustion: extra.eng4Combustion,
    eng1FlowPph: extra.eng1FlowPph,
    eng2FlowPph: extra.eng2FlowPph,
    eng3FlowPph: extra.eng3FlowPph,
    eng4FlowPph: extra.eng4FlowPph,
    unlimitedFuel: extra.unlimitedFuel,
    fuelDumpActive: extra.fuelDumpActive,
    tankCenterLb: extra.tankCenterLb,
    tankLeftMainLb: extra.tankLeftMainLb,
    tankRightMainLb: extra.tankRightMainLb,
    tankLeftAuxLb: extra.tankLeftAuxLb,
    tankRightAuxLb: extra.tankRightAuxLb,
    source: "simconnect",
  };
}

function combustionSeen(current: boolean | null | undefined, seen: boolean): boolean {
  return seen || current === true || current === false;
}

function anyPower(sample: SimTelemetrySample): boolean {
  const combustion = [sample.eng1Combustion, sample.eng2Combustion, sample.eng3Combustion, sample.eng4Combustion];
  if (combustion.some((value) => value === true)) return true;
  if (apuRunning(sample).running) return true;
  // Combustion is the engine truth when published; N1 still spooling down after shutdown is not power.
  if (combustion.some((value) => value === false)) return false;
  const n1 = [sample.eng1N1, sample.eng2N1].filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  return n1.some((value) => value >= N1_RUNNING_PCT);
}

function advanceFuelTrack(prev: FuelTrack, sample: SimTelemetrySample, accept: boolean, dt: number): FuelTrack {
  const engSeen: [boolean, boolean, boolean, boolean] = [
    combustionSeen(sample.eng1Combustion, prev.engSeen[0]),
    combustionSeen(sample.eng2Combustion, prev.engSeen[1]),
    combustionSeen(sample.eng3Combustion, prev.engSeen[2]),
    combustionSeen(sample.eng4Combustion, prev.engSeen[3]),
  ];
  const apuEverPublished = prev.apuEverPublished || (sample.apuPctRpm != null && Number.isFinite(sample.apuPctRpm));
  let flowLb = prev.flowLb;
  let flowSeen = prev.flowSeen;
  const pph = engineFlowPph(sample);
  if (pph != null && accept && dt > 0) {
    flowLb += pph * (dt / 3_600_000);
    flowSeen = true;
  }
  const gate = shutdownGate({
    combustion: [sample.eng1Combustion, sample.eng2Combustion, sample.eng3Combustion, sample.eng4Combustion],
    seen: engSeen,
    n1: [sample.eng1N1, sample.eng2N1],
    apuPctRpm: sample.apuPctRpm,
    apuEverPublished,
  });
  return {
    flowLb,
    flowSeen,
    unlimited: prev.unlimited || sample.unlimitedFuel === true,
    dump: prev.dump || sample.fuelDumpActive === true,
    engSeen,
    apuEverPublished,
    sawPower: prev.sawPower || anyPower(sample),
    shutdownLatched: gate.ready ? prev.shutdownLatched : false,
    tankLoad: prev.tankLoad,
  };
}

function tanksFromSample(sample: SimTelemetrySample): FuelTankLb[] {
  const pairs: [string, number | null | undefined][] = [
    ["center", sample.tankCenterLb],
    ["left_main", sample.tankLeftMainLb],
    ["right_main", sample.tankRightMainLb],
    ["left_aux", sample.tankLeftAuxLb],
    ["right_aux", sample.tankRightAuxLb],
  ];
  return pairs.flatMap(([id, lb]) => (typeof lb === "number" && Number.isFinite(lb) && lb >= 0 ? [{ id, lb }] : []));
}

function emptySession(sample: SimTelemetrySample): TailTelemetrySession {
  const fuel = sample.fuelQtyLb ?? undefined;
  return {
    registration: sample.registration,
    aircraftId: sample.aircraftId,
    tripId: sample.tripId,
    flightLogId: sample.flightLogId,
    callsign: sample.callsign,
    originIcao: sample.originIcao,
    destIcao: sample.destIcao,
    startedAtUtc: sample.atUtc,
    lastAtUtc: sample.atUtc,
    airTimeMs: 0,
    blockMs: 0,
    engineMs: 0,
    apuMs: 0,
    fuelStartLb: fuel,
    fuelLastLb: fuel,
    fuelBurnedLb: 0,
    engineSource: engineRunning(sample).source,
    apuReliable: apuRunning(sample).reliable,
    airborneSeen: sampleIsAirborne(sample),
    offBlocksUtc: sampleIsMoving(sample) ? sample.atUtc : undefined,
    airborneUtc: sampleIsAirborne(sample) ? sample.atUtc : undefined,
    fuelAirborneLb: sampleIsAirborne(sample) ? fuel : undefined,
    airFlowLb: 0,
    lastAirborne: sampleIsAirborne(sample),
    lastEngineRunning: engineRunning(sample).running,
    lastApuRunning: apuRunning(sample).running,
    fuelTrack: advanceFuelTrack(emptyFuelTrack(), sample, false, 0),
    engineMsEach: [0, 0, 0, 0],
    lastEngineOn: combustionFlags(sample).map((flag) => flag === true),
    engineCount: sample.engineCount != null ? clampEngineCount(sample.engineCount) : undefined,
    combustionTrueSeen: combustionFlags(sample).some((flag) => flag === true),
  };
}

function combustionFlags(sample: SimTelemetrySample): Array<boolean | null | undefined> {
  return [sample.eng1Combustion, sample.eng2Combustion, sample.eng3Combustion, sample.eng4Combustion];
}

function maxGap(prev: SimTelemetrySample, cur: SimTelemetrySample): number {
  if (sampleIsAirborne(prev) && sampleIsAirborne(cur)) return SIM_DRAFT_AIR_GAP_MS;
  if (sampleIsMoving(prev) && sampleIsMoving(cur)) return SIM_DRAFT_TAXI_GAP_MS;
  return SIM_DRAFT_GAP_MS;
}

export function applyTelemetrySample(
  session: TailTelemetrySession | null,
  sample: SimTelemetrySample,
  opts?: { backfill?: boolean },
): TailTelemetrySession {
  const row = hydrateSimTelemetrySample(sample);
  if (!row) return session ?? emptySession(sample);
  if (!session || session.closed || normalizeTailKey(session.registration) !== normalizeTailKey(row.registration)) {
    return emptySession(row);
  }

  const prevT = sampleTime(session.lastAtUtc);
  const curT = sampleTime(row.atUtc);
  const dt = curT - prevT;
  if (!Number.isFinite(dt) || dt < 0) return session;
  if (dt < TELEMETRY_MIN_DT_MS) {
    return {
      ...session,
      lastAtUtc: row.atUtc,
      aircraftId: row.aircraftId ?? session.aircraftId,
      fuelTrack: advanceFuelTrack(session.fuelTrack ?? emptyFuelTrack(), row, false, 0),
    };
  }

  const gap = maxGap(
    {
      ...row,
      atUtc: session.lastAtUtc,
    },
    row,
  );
  const accept = opts?.backfill
    ? dt > 0 && dt <= DESK_GAP_BACKFILL_MS
    : dt > 0 && dt <= gap;

  let airTimeMs = session.airTimeMs;
  let engineMs = session.engineMs;
  let apuMs = session.apuMs;
  let blockMs = session.blockMs;
  const count = clampEngineCount(row.engineCount ?? session.engineCount ?? 2);
  const engineMsEach = [...(session.engineMsEach ?? [0, 0, 0, 0])];
  while (engineMsEach.length < 4) engineMsEach.push(0);
  const lastOn = [...(session.lastEngineOn ?? [false, false, false, false])];
  const eng = engineRunning(row);
  const apu = apuRunning(row);
  if (accept) {
    if (session.lastAirborne) airTimeMs += dt;
    if (session.lastEngineRunning) engineMs += dt;
    if (session.apuReliable && session.lastApuRunning) apuMs += dt;
    if (session.lastAirborne || (session.offBlocksUtc && !session.onBlocksUtc)) blockMs += dt;
    for (let index = 0; index < count; index += 1) {
      if (lastOn[index]) engineMsEach[index] = (engineMsEach[index] ?? 0) + dt;
    }
  }
  const flags = combustionFlags(row);
  const combustionTrueSeen = session.combustionTrueSeen || flags.slice(0, count).some((flag) => flag === true);

  let fuelBurnedLb = session.fuelBurnedLb;
  let fuelStartLb = session.fuelStartLb;
  let fuelLastLb = session.fuelLastLb;
  if (row.fuelQtyLb != null) {
    if (fuelStartLb == null) fuelStartLb = row.fuelQtyLb;
    if (fuelLastLb != null && row.fuelQtyLb < fuelLastLb) {
      fuelBurnedLb += fuelLastLb - row.fuelQtyLb;
    }
    fuelLastLb = row.fuelQtyLb;
  }
  const fuelTrack = advanceFuelTrack(session.fuelTrack ?? emptyFuelTrack(), row, accept, dt);

  const airborneSeen = session.airborneSeen || sampleIsAirborne(row);
  let offBlocksUtc = session.offBlocksUtc;
  let airborneUtc = session.airborneUtc;
  let landedUtc = session.landedUtc;
  let onBlocksUtc = session.onBlocksUtc;
  let fuelAirborneLb = session.fuelAirborneLb;
  let fuelLandedLb = session.fuelLandedLb;
  let airFlowLb = session.airFlowLb ?? 0;
  const airPph = engineFlowPph(row);
  if (accept && session.lastAirborne && airPph != null) airFlowLb += airPph * (dt / 3_600_000);
  if (!offBlocksUtc && sampleIsMoving(row)) offBlocksUtc = row.atUtc;
  if (!airborneUtc && sampleIsAirborne(row)) {
    airborneUtc = row.atUtc;
    fuelAirborneLb = row.fuelQtyLb ?? session.fuelLastLb;
  }
  if (airborneSeen && !landedUtc && !sampleIsAirborne(row) && session.airborneSeen) {
    landedUtc = row.atUtc;
    fuelLandedLb = row.fuelQtyLb ?? session.fuelLastLb;
  }
  if (sampleIsMoving(row)) onBlocksUtc = undefined;
  else if (offBlocksUtc && airborneSeen && !onBlocksUtc) onBlocksUtc = row.atUtc;

  // Once the tail has flown, the session belongs to that leg until shutdown —
  // the desk moving on to the next ticket must not re-tag taxi-in and shutdown.
  const pick = <T>(fromRow: T | undefined, held: T | undefined) =>
    session.airborneSeen ? (held ?? fromRow) : (fromRow ?? held);
  return {
    ...session,
    aircraftId: row.aircraftId ?? session.aircraftId,
    tripId: pick(row.tripId, session.tripId),
    flightLogId: pick(row.flightLogId, session.flightLogId),
    callsign: pick(row.callsign, session.callsign),
    originIcao: pick(row.originIcao, session.originIcao),
    destIcao: pick(row.destIcao, session.destIcao),
    lastAtUtc: row.atUtc,
    airTimeMs,
    blockMs,
    engineMs,
    apuMs,
    fuelStartLb,
    fuelLastLb,
    fuelBurnedLb,
    engineSource: eng.source === "phase_fallback" ? session.engineSource : eng.source,
    apuReliable: session.apuReliable || apu.reliable,
    airborneSeen,
    offBlocksUtc,
    airborneUtc,
    landedUtc,
    onBlocksUtc,
    fuelAirborneLb,
    fuelLandedLb,
    airFlowLb,
    lastAirborne: sampleIsAirborne(row),
    lastEngineRunning: eng.running,
    lastApuRunning: apu.running,
    fuelTrack,
    engineMsEach,
    lastEngineOn: flags.map((flag) => flag === true),
    engineCount: count,
    combustionTrueSeen,
    careerBefore: session.careerBefore,
  };
}

/**
 * A flown leg closes at the gate: post-landing, stationary, engines off.
 * Rollout and taxi-in (also phase `parked`) stay in the leg so on-blocks,
 * engine shutdown and the taxi-in burn land on the right trip.
 */
export function sessionShouldClose(session: TailTelemetrySession, sample: SimTelemetrySample): boolean {
  if (session.closed) return false;
  if (!session.airborneSeen) return false;
  if (sampleIsAirborne(sample)) return false;
  if (sample.phase !== "parked") return false;
  if (sampleIsMoving(sample)) return false;
  return !engineRunning(sample).running;
}

/** Sim left at the gate with engines running, then reconnected much later. */
function sessionAbandonedAtGate(session: TailTelemetrySession, sample: SimTelemetrySample): boolean {
  if (session.closed || !session.airborneSeen || !session.onBlocksUtc || session.lastAirborne) return false;
  const gap = sampleTime(sample.atUtc) - sampleTime(session.lastAtUtc);
  return Number.isFinite(gap) && gap > SIM_DRAFT_GAP_MS;
}

/**
 * The desk process restarted while the sim stayed up. Fill the missed window
 * from the last airborne / combustion flags instead of dropping it.
 */
export function shouldBackfillDeskGap(
  session: TailTelemetrySession | null | undefined,
  sample: Pick<SimTelemetrySample, "atUtc">,
  deskBootMs: number,
): boolean {
  if (!session || session.closed) return false;
  if (sessionAbandonedAtGate(session, sample as SimTelemetrySample)) return false;
  const last = sampleTime(session.lastAtUtc);
  const cur = sampleTime(sample.atUtc);
  if (!Number.isFinite(last) || !Number.isFinite(cur)) return false;
  if (!(last < deskBootMs && cur >= deskBootMs)) return false;
  const gap = cur - last;
  return gap > 0 && gap <= DESK_GAP_BACKFILL_MS;
}

export function sessionBlockMs(session: TailTelemetrySession): number {
  const off = session.offBlocksUtc ? sampleTime(session.offBlocksUtc) : Number.NaN;
  const on = session.onBlocksUtc ? sampleTime(session.onBlocksUtc) : Number.NaN;
  if (Number.isFinite(off) && Number.isFinite(on) && on > off) return on - off;
  return session.blockMs || session.engineMs;
}

function tankDeltaLb(session: TailTelemetrySession): number | null {
  if (session.fuelBurnedLb > 0) return session.fuelBurnedLb;
  if (session.fuelStartLb != null && session.fuelLastLb != null && session.fuelStartLb > session.fuelLastLb) {
    return session.fuelStartLb - session.fuelLastLb;
  }
  if (session.fuelStartLb != null && session.fuelLastLb != null) return session.fuelBurnedLb;
  return null;
}

function measuredBurn(session: TailTelemetrySession) {
  const track = session.fuelTrack ?? emptyFuelTrack();
  return resolveMeasuredBurn({
    tankLb: tankDeltaLb(session),
    flowLb: track.flowSeen ? track.flowLb : null,
    flowSeen: track.flowSeen,
    unlimitedFuel: track.unlimited,
    fuelDump: track.dump,
  });
}

function fuelUsedFromSession(session: TailTelemetrySession): number | undefined {
  const measured = measuredBurn(session);
  if (!measured.reliable || measured.burnLb == null || measured.burnLb <= 0) return undefined;
  return measured.burnLb;
}

export function closeTelemetrySession(session: TailTelemetrySession, endedAtUtc?: string): TailTripTelemetry | null {
  if (!session.airborneSeen && session.engineMs <= 0 && session.airTimeMs <= 0) return null;
  const end = endedAtUtc ?? session.lastAtUtc;
  const measured = measuredBurn(session);
  const fuelUsed = measured.reliable && measured.burnLb != null && measured.burnLb > 0 ? measured.burnLb : undefined;
  const idBase = session.tripId || session.flightLogId || `${normalizeTailKey(session.registration)}-${session.startedAtUtc}`;
  return {
    id: `tel-${idBase}`,
    registration: session.registration,
    aircraftId: session.aircraftId,
    tripId: session.tripId,
    flightLogId: session.flightLogId,
    callsign: session.callsign,
    originIcao: session.originIcao,
    destIcao: session.destIcao,
    startedAtUtc: session.startedAtUtc,
    endedAtUtc: end,
    airTimeHours: hoursFromMs(session.airTimeMs),
    blockHours: hoursFromMs(sessionBlockMs(session)),
    engineHours: hoursFromMs(session.engineMs),
    ...engineMxFromSession(session),
    apuHours: session.apuReliable ? hoursFromMs(session.apuMs) : null,
    fuelStartAmount: session.fuelStartLb,
    fuelEndAmount: session.fuelLastLb,
    fuelUsedAmount: fuelUsed,
    fuelUnit: "lb",
    engineSource: session.engineSource,
    apuReliable: session.apuReliable,
    source: "simconnect",
    offBlocksUtc: session.offBlocksUtc,
    airborneUtc: session.airborneUtc,
    landedUtc: session.landedUtc,
    onBlocksUtc: session.onBlocksUtc,
    fuelBurnReliable: measured.reliable,
    fuelBurnReason: measured.reason,
    careerBefore: session.careerBefore,
  };
}

export function emptyTotals(registration: string, aircraftId?: string): TailTelemetryTotals {
  return {
    registration,
    aircraftId,
    airTimeHours: 0,
    blockHours: 0,
    engineHours: 0,
    apuHours: 0,
    engineSource: "phase_fallback",
    apuReliable: false,
    tripCount: 0,
  };
}

export function addTripToTotals(totals: TailTelemetryTotals, trip: TailTripTelemetry): TailTelemetryTotals {
  return {
    ...totals,
    aircraftId: trip.aircraftId ?? totals.aircraftId,
    airTimeHours: Math.round((totals.airTimeHours + trip.airTimeHours) * 100) / 100,
    blockHours: Math.round((totals.blockHours + trip.blockHours) * 100) / 100,
    engineHours: Math.round((totals.engineHours + trip.engineHours) * 100) / 100,
    apuHours: Math.round((totals.apuHours + (trip.apuHours ?? 0)) * 100) / 100,
    lastAtUtc: trip.endedAtUtc,
    engineSource: trip.engineSource,
    apuReliable: totals.apuReliable || trip.apuReliable,
    tripCount: totals.tripCount + 1,
  };
}

export function subtractTripFromTotals(totals: TailTelemetryTotals, trip: TailTripTelemetry): TailTelemetryTotals {
  const round = (value: number) => Math.max(0, Math.round(value * 100) / 100);
  return {
    ...totals,
    airTimeHours: round(totals.airTimeHours - trip.airTimeHours),
    blockHours: round(totals.blockHours - trip.blockHours),
    engineHours: round(totals.engineHours - trip.engineHours),
    apuHours: round(totals.apuHours - (trip.apuHours ?? 0)),
    tripCount: Math.max(0, totals.tripCount - 1),
  };
}

export function stripFlightTelemetry(
  state: TailTelemetryState,
  missionId: string,
  flightLogId?: string,
): TailTelemetryState {
  const owns = (row: { tripId?: string; flightLogId?: string }) =>
    row.tripId === missionId || Boolean(flightLogId && row.flightLogId === flightLogId);
  const removed = state.trips.filter(owns);
  const sessionOwns = Boolean(state.session && owns(state.session));
  if (!removed.length && !sessionOwns) return state;
  const totals = { ...state.totals };
  for (const trip of removed) {
    const key = normalizeTailKey(trip.registration);
    const prev = totals[key];
    if (prev) totals[key] = subtractTripFromTotals(prev, trip);
  }
  return {
    ...state,
    session: sessionOwns ? null : state.session,
    trips: state.trips.filter((trip) => !owns(trip)),
    totals,
  };
}

export function upsertTripList(trips: TailTripTelemetry[], trip: TailTripTelemetry): TailTripTelemetry[] {
  const next = trips.filter((row) => row.id !== trip.id);
  next.push(trip);
  next.sort((a, b) => sampleTime(a.startedAtUtc) - sampleTime(b.startedAtUtc));
  return next.length > TAIL_TRIP_TELEMETRY_MAX ? next.slice(next.length - TAIL_TRIP_TELEMETRY_MAX) : next;
}

export function findTripTelemetry(
  trips: readonly TailTripTelemetry[],
  hint: { tripId?: string; flightLogId?: string; registration?: string },
): TailTripTelemetry | undefined {
  if (hint.tripId) {
    const hit = [...trips].reverse().find((t) => t.tripId === hint.tripId);
    if (hit) return hit;
  }
  if (hint.flightLogId) {
    const hit = [...trips].reverse().find((t) => t.flightLogId === hint.flightLogId);
    if (hit) return hit;
  }
  if (hint.registration) {
    const key = normalizeTailKey(hint.registration);
    return [...trips].reverse().find((t) => normalizeTailKey(t.registration) === key);
  }
  return undefined;
}

export function tripFuelUsedGal(
  trip?: Pick<TailTripTelemetry, "fuelUsedAmount" | "fuelUnit" | "fuelBurnReliable"> | null,
): number | null {
  if (!trip || trip.fuelBurnReliable === false) return null;
  if (trip.fuelUsedAmount == null || !Number.isFinite(trip.fuelUsedAmount) || trip.fuelUsedAmount <= 0) {
    return null;
  }
  const unit = isFuelUnit(trip.fuelUnit) ? trip.fuelUnit : "lb";
  return fuelUsedToUsGal(trip.fuelUsedAmount, unit);
}

/**
 * Wheels-off → touchdown tank decrease and engine flow, the span SimBrief trip
 * fuel covers. APU and taxi burn stay out. Null before wheels-off.
 */
function airborneBurn(session: TailTelemetrySession): { tankLb: number | null; flowLb: number | null } | null {
  if (!session.airborneSeen) return { tankLb: null, flowLb: null };
  if (session.fuelAirborneLb == null) return null;
  const end = session.fuelLandedLb ?? session.fuelLastLb;
  const tankLb = end != null ? Math.max(0, session.fuelAirborneLb - end) : null;
  return { tankLb, flowLb: session.airFlowLb ?? 0 };
}

/** Live Burn vs plan measure: airborne span only (whole session for a leg opened mid-air before this was tracked). */
export function sessionFuelMeasure(session: TailTelemetrySession | null): {
  tankLb: number | null;
  flowLb: number | null;
  flowSeen: boolean;
  unlimitedFuel: boolean;
  fuelDump: boolean;
  tankLoad?: TankLoadKind;
  active: boolean;
} | null {
  if (!session) return null;
  const track = session.fuelTrack ?? emptyFuelTrack();
  const sessionTankLb = tankDeltaLb(session);
  const air = airborneBurn(session);
  const tankLb = air ? air.tankLb : sessionTankLb;
  const flowLb = air ? air.flowLb : track.flowLb;
  return {
    tankLb,
    flowLb: track.flowSeen ? flowLb : null,
    flowSeen: track.flowSeen,
    unlimitedFuel: track.unlimited,
    fuelDump: track.dump,
    tankLoad: track.tankLoad,
    active: session.airborneSeen || track.sawPower || (sessionTankLb != null && sessionTankLb > 0),
  };
}

export function lastLandingTanksLb(state: TailTelemetryState, registration?: string | null): number | null {
  const key = normalizeTailKey(registration);
  if (!key) return null;
  const snap = state.shutdownByTail?.[key];
  if (snap && Number.isFinite(snap.fuelQtyLb) && snap.fuelQtyLb >= 0) return snap.fuelQtyLb;
  const trip = [...state.trips].reverse().find((row) => {
    if (normalizeTailKey(row.registration) !== key) return false;
    if (row.fuelBurnReason === "unlimited_fuel") return false;
    return typeof row.fuelEndAmount === "number" && Number.isFinite(row.fuelEndAmount) && row.fuelEndAmount >= 0;
  });
  if (!trip || trip.fuelEndAmount == null) return null;
  const unit = isFuelUnit(trip.fuelUnit) ? trip.fuelUnit : "lb";
  return unit === "lb" ? trip.fuelEndAmount : convertFuel(trip.fuelEndAmount, unit, "lb");
}

function noteShutdown(
  session: TailTelemetrySession,
  row: SimTelemetrySample,
  shutdownByTail: Record<string, FuelShutdownSnapshot>,
  key: string,
): { session: TailTelemetrySession; shutdownByTail: Record<string, FuelShutdownSnapshot> } {
  const track = session.fuelTrack ?? emptyFuelTrack();
  const gate = shutdownGate({
    combustion: [row.eng1Combustion, row.eng2Combustion, row.eng3Combustion, row.eng4Combustion],
    seen: track.engSeen,
    n1: [row.eng1N1, row.eng2N1],
    apuPctRpm: row.apuPctRpm,
    apuEverPublished: track.apuEverPublished,
  });
  if (!gate.ready || !track.sawPower || track.shutdownLatched || track.unlimited || row.fuelQtyLb == null) {
    return { session, shutdownByTail };
  }
  return {
    shutdownByTail: {
      ...shutdownByTail,
      [key]: {
        registration: row.registration,
        atUtc: row.atUtc,
        fuelQtyLb: row.fuelQtyLb,
        tanks: tanksFromSample(row),
      },
    },
    session: { ...session, fuelTrack: { ...track, shutdownLatched: true } },
  };
}

/**
 * Dollar reserves use measured engine time (hundredths). `engineMxHours` is the
 * hobbs tenth and can sit above that figure. Fall back to the hobbs / block /
 * air chain only when measured engine time is not on the trip.
 */
export function tripEngineHoursForReserve(
  trip?: Pick<TailTripTelemetry, "engineHours" | "engineMxHours" | "blockHours" | "airTimeHours"> | null,
): number | null {
  if (trip && typeof trip.engineHours === "number" && Number.isFinite(trip.engineHours) && trip.engineHours > 0) {
    return trip.engineHours;
  }
  return tripHoursForQuote(trip);
}

/** Billable / reserve hours from a closed sim trip — engine, then block, then air. */
export function tripHoursForQuote(
  trip?: Pick<TailTripTelemetry, "engineHours" | "engineMxHours" | "blockHours" | "airTimeHours"> | null,
): number | null {
  if (!trip) return null;
  if (typeof trip.engineMxHours === "number" && Number.isFinite(trip.engineMxHours) && trip.engineMxHours > 0) {
    return trip.engineMxHours;
  }
  if (typeof trip.engineHours === "number" && Number.isFinite(trip.engineHours) && trip.engineHours > 0) {
    return trip.engineHours;
  }
  if (typeof trip.blockHours === "number" && Number.isFinite(trip.blockHours) && trip.blockHours > 0) {
    return trip.blockHours;
  }
  if (typeof trip.airTimeHours === "number" && Number.isFinite(trip.airTimeHours) && trip.airTimeHours > 0) {
    return trip.airTimeHours;
  }
  return null;
}

export function finalizeTelemetrySession(state: TailTelemetryState, endedAtUtc?: string): TailTelemetryState {
  if (!state.session) return state;
  const closed = closeTelemetrySession(state.session, endedAtUtc);
  if (!closed) return { ...state, session: null };
  const key = normalizeTailKey(closed.registration);
  const prev = state.totals[key] ?? emptyTotals(closed.registration, closed.aircraftId);
  return {
    session: null,
    totals: { ...state.totals, [key]: addTripToTotals(prev, closed) },
    trips: upsertTripList(state.trips, closed),
    shutdownByTail: state.shutdownByTail,
  };
}

export function applyTelemetryTick(
  state: TailTelemetryState,
  sample: SimTelemetrySample,
  opts?: { backfill?: boolean; careerBefore?: TailCareerBefore | null },
): TailTelemetryState {
  const row = hydrateSimTelemetrySample(sample);
  if (!row) return state;
  const key = normalizeTailKey(row.registration);
  let session = state.session;
  let totals = state.totals;
  let trips = state.trips;
  let shutdownByTail = state.shutdownByTail ?? {};

  if (session && (normalizeTailKey(session.registration) !== key || sessionAbandonedAtGate(session, row))) {
    const closed = closeTelemetrySession(session);
    if (closed) {
      trips = upsertTripList(trips, closed);
      const prev = totals[normalizeTailKey(closed.registration)] ?? emptyTotals(closed.registration, closed.aircraftId);
      totals = { ...totals, [normalizeTailKey(closed.registration)]: addTripToTotals(prev, closed) };
    }
    session = null;
  }

  const opened = !session;
  let nextSession = applyTelemetrySample(session, row, { backfill: Boolean(opts?.backfill) });
  if (opened && opts?.careerBefore) {
    nextSession = { ...nextSession, careerBefore: opts.careerBefore };
  }
  if (opened) {
    const track = nextSession.fuelTrack ?? emptyFuelTrack();
    nextSession = {
      ...nextSession,
      fuelTrack: {
        ...track,
        tankLoad: classifyTankLoad(shutdownByTail[key]?.fuelQtyLb ?? null, row.fuelQtyLb),
      },
    };
  }
  const noted = noteShutdown(nextSession, row, shutdownByTail, key);
  nextSession = noted.session;
  shutdownByTail = noted.shutdownByTail;
  if (session && sessionShouldClose(session, row)) {
    const closed = closeTelemetrySession({ ...nextSession, closed: true }, row.atUtc);
    if (closed) {
      trips = upsertTripList(trips, closed);
      const prev = totals[key] ?? emptyTotals(row.registration, row.aircraftId);
      totals = { ...totals, [key]: addTripToTotals(prev, closed) };
    }
    return { session: null, totals, trips, shutdownByTail };
  }

  return { session: nextSession, totals, trips, shutdownByTail };
}

/** Immediate zustand write on session open/close/tail switch; otherwise coalesce. */
export function telemetryStoreShouldFlush(
  prev: TailTelemetryState,
  next: TailTelemetryState,
  lastFlushAtMs: number,
  nowMs: number,
): boolean {
  if (next === prev) return false;
  if (next.trips !== prev.trips || next.totals !== prev.totals || next.shutdownByTail !== prev.shutdownByTail) return true;
  if (Boolean(prev.session) !== Boolean(next.session)) return true;
  if (normalizeTailKey(prev.session?.registration) !== normalizeTailKey(next.session?.registration)) return true;
  if (Boolean(prev.session?.closed) !== Boolean(next.session?.closed)) return true;
  return nowMs - lastFlushAtMs >= TELEMETRY_STORE_MIN_MS;
}

export type LiveTelemetryBinding = Pick<SimTelemetrySample, "tripId" | "flightLogId" | "callsign" | "originIcao" | "destIcao">;

/**
 * Which ticket / log a live sim sample on `registration` belongs to: the
 * tail's in-progress ticket (or an in-progress ticket with no tail yet), else
 * its earliest open ticket. The log is always that ticket's log, never another trip's.
 */
export function liveTelemetryBinding(input: {
  missions: readonly Mission[];
  logs: readonly FlightLogEntry[];
  registration?: string | null;
}): LiveTelemetryBinding {
  const key = normalizeTailKey(input.registration);
  const onTail = key ? input.missions.filter((m) => normalizeTailKey(m.registration) === key) : [];
  const open = onTail
    .filter((m) => m.status !== "completed" && m.status !== "cancelled")
    .sort((a, b) => (sampleTime(a.departureIso ?? "") || 0) - (sampleTime(b.departureIso ?? "") || 0));
  const flying =
    onTail.find((m) => m.status === "in-progress") ??
    input.missions.find((m) => m.status === "in-progress" && !normalizeTailKey(m.registration)) ??
    open[0];
  const liveLog = (l: FlightLogEntry) => l.status === "airborne" || l.status === "taxi" || l.status === "filed";
  const log = flying
    ? input.logs.find((l) => l.tripId === flying.id)
    : input.logs.find((l) => liveLog(l) && (!key || normalizeTailKey(l.registration) === key));
  return {
    tripId: flying?.id ?? log?.tripId,
    flightLogId: log?.id,
    callsign: flying?.callsign ?? log?.callsign,
    originIcao: flying?.originIcao ?? log?.originIcao,
    destIcao: flying?.destIcao ?? log?.destIcao,
  };
}

export function resolveLiveTelemetryTail(input: {
  fleet: readonly AircraftSpec[];
  missions?: readonly Mission[];
  logs?: readonly FlightLogEntry[];
  preferredReg?: string;
}): { registration: string; aircraftId?: string } | null {
  const fleet = input.fleet;
  if (!fleet.length) return null;
  const byReg = (reg?: string) => {
    const key = normalizeTailKey(reg);
    if (!key) return undefined;
    return fleet.find((a) => normalizeTailKey(a.registration) === key);
  };

  const flying =
    input.missions?.find((m) => m.status === "in-progress") ??
    input.missions?.find((m) => m.status === "pending" && Boolean(m.registration));
  const fromMission = byReg(flying?.registration);
  if (fromMission) return { registration: fromMission.registration, aircraftId: fromMission.id };

  const open = input.logs?.find((l) => l.status === "airborne" || l.status === "taxi" || l.status === "filed");
  const fromLog = byReg(open?.registration);
  if (fromLog) return { registration: fromLog.registration, aircraftId: fromLog.id };

  const preferred = byReg(input.preferredReg);
  if (preferred) return { registration: preferred.registration, aircraftId: preferred.id };

  const hawker =
    byReg(SUGGESTED_PRIMARY_REG) ??
    fleet.find((a) => a.icaoType === "H25B") ??
    fleet[0];
  return hawker ? { registration: hawker.registration, aircraftId: hawker.id } : null;
}

function hydrateTotals(raw: unknown): TailTelemetryTotals | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const registration = displayRegistration(typeof row.registration === "string" ? row.registration : "");
  if (!registration) return null;
  const engineSource: EngineTimeSource =
    row.engineSource === "combustion" || row.engineSource === "n1" || row.engineSource === "phase_fallback"
      ? row.engineSource
      : "phase_fallback";
  return {
    registration,
    aircraftId: typeof row.aircraftId === "string" ? row.aircraftId : undefined,
    airTimeHours: asNum(row.airTimeHours) ?? 0,
    blockHours: asNum(row.blockHours) ?? 0,
    engineHours: asNum(row.engineHours) ?? 0,
    apuHours: asNum(row.apuHours) ?? 0,
    lastAtUtc: typeof row.lastAtUtc === "string" ? row.lastAtUtc : undefined,
    engineSource,
    apuReliable: row.apuReliable === true,
    tripCount: Math.max(0, Math.round(asNum(row.tripCount) ?? 0)),
  };
}

function hydrateTrip(raw: unknown): TailTripTelemetry | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const registration = displayRegistration(typeof row.registration === "string" ? row.registration : "");
  const startedAtUtc = typeof row.startedAtUtc === "string" ? row.startedAtUtc : "";
  const endedAtUtc = typeof row.endedAtUtc === "string" ? row.endedAtUtc : "";
  if (!registration || !startedAtUtc) return null;
  const engineSource: EngineTimeSource =
    row.engineSource === "combustion" || row.engineSource === "n1" || row.engineSource === "phase_fallback"
      ? row.engineSource
      : "phase_fallback";
  const fuelUnit: FuelUnit = isFuelUnit(row.fuelUnit) ? row.fuelUnit : "lb";
  return {
    id: typeof row.id === "string" && row.id ? row.id : `tel-${normalizeTailKey(registration)}-${startedAtUtc}`,
    registration,
    aircraftId: typeof row.aircraftId === "string" ? row.aircraftId : undefined,
    tripId: typeof row.tripId === "string" ? row.tripId : undefined,
    flightLogId: typeof row.flightLogId === "string" ? row.flightLogId : undefined,
    callsign: typeof row.callsign === "string" ? row.callsign : undefined,
    originIcao: typeof row.originIcao === "string" ? row.originIcao : undefined,
    destIcao: typeof row.destIcao === "string" ? row.destIcao : undefined,
    startedAtUtc,
    endedAtUtc: endedAtUtc || startedAtUtc,
    airTimeHours: asNum(row.airTimeHours) ?? 0,
    blockHours: asNum(row.blockHours) ?? 0,
    engineHours: asNum(row.engineHours) ?? 0,
    apuHours: asNum(row.apuHours),
    fuelStartAmount: asNum(row.fuelStartAmount) ?? undefined,
    fuelEndAmount: asNum(row.fuelEndAmount) ?? undefined,
    fuelUsedAmount: asNum(row.fuelUsedAmount) ?? undefined,
    fuelUnit,
    engineSource,
    apuReliable: row.apuReliable === true,
    source: "simconnect",
    fuelBurnReliable: row.fuelBurnReliable === false ? false : undefined,
    fuelBurnReason:
      row.fuelBurnReason === "unlimited_fuel" || row.fuelBurnReason === "fuel_dump" || row.fuelBurnReason === "crosscheck"
        ? row.fuelBurnReason
        : null,
    offBlocksUtc: typeof row.offBlocksUtc === "string" ? row.offBlocksUtc : undefined,
    airborneUtc: typeof row.airborneUtc === "string" ? row.airborneUtc : undefined,
    landedUtc: typeof row.landedUtc === "string" ? row.landedUtc : undefined,
    onBlocksUtc: typeof row.onBlocksUtc === "string" ? row.onBlocksUtc : undefined,
    engineMxHours: asNum(row.engineMxHours) ?? undefined,
    engineHoursEach: hydrateHourList(row.engineHoursEach),
    engineDetail: typeof row.engineDetail === "string" ? row.engineDetail : undefined,
    careerBefore: hydrateCareerBefore(row.careerBefore),
  };
}

function hydrateHourList(raw: unknown): number[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const hours = raw.map((value) => asNum(value)).filter((value): value is number => value != null && value >= 0);
  return hours.length ? hours : undefined;
}

function hydrateCareerBefore(raw: unknown): TailCareerBefore | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const row = raw as Record<string, unknown>;
  const airframeHours = asNum(row.airframeHours);
  if (airframeHours == null) return undefined;
  const status =
    row.status === "available" || row.status === "grounded" || row.status === "ferry" || row.status === "in_mx"
      ? row.status
      : "available";
  return {
    fuelOnBoardGal: asNum(row.fuelOnBoardGal) ?? undefined,
    airframeHours,
    engineHours: asNum(row.engineHours) ?? undefined,
    apuHours: asNum(row.apuHours) ?? undefined,
    engineHoursEach: hydrateHourList(row.engineHoursEach),
    cycles: asNum(row.cycles) ?? undefined,
    lastAirportIcao: typeof row.lastAirportIcao === "string" ? row.lastAirportIcao : undefined,
    status,
    available: row.available !== false,
  };
}

function hydrateSession(raw: unknown): TailTelemetrySession | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const registration = displayRegistration(typeof row.registration === "string" ? row.registration : "");
  const startedAtUtc = typeof row.startedAtUtc === "string" ? row.startedAtUtc : "";
  const lastAtUtc = typeof row.lastAtUtc === "string" ? row.lastAtUtc : startedAtUtc;
  if (!registration || !startedAtUtc) return null;
  const engineSource: EngineTimeSource =
    row.engineSource === "combustion" || row.engineSource === "n1" || row.engineSource === "phase_fallback"
      ? row.engineSource
      : "phase_fallback";
  return {
    registration,
    aircraftId: typeof row.aircraftId === "string" ? row.aircraftId : undefined,
    tripId: typeof row.tripId === "string" ? row.tripId : undefined,
    flightLogId: typeof row.flightLogId === "string" ? row.flightLogId : undefined,
    callsign: typeof row.callsign === "string" ? row.callsign : undefined,
    originIcao: typeof row.originIcao === "string" ? row.originIcao : undefined,
    destIcao: typeof row.destIcao === "string" ? row.destIcao : undefined,
    startedAtUtc,
    lastAtUtc,
    airTimeMs: asNum(row.airTimeMs) ?? 0,
    blockMs: asNum(row.blockMs) ?? 0,
    engineMs: asNum(row.engineMs) ?? 0,
    apuMs: asNum(row.apuMs) ?? 0,
    fuelStartLb: asNum(row.fuelStartLb) ?? undefined,
    fuelLastLb: asNum(row.fuelLastLb) ?? undefined,
    fuelBurnedLb: asNum(row.fuelBurnedLb) ?? 0,
    engineSource,
    apuReliable: row.apuReliable === true,
    airborneSeen: row.airborneSeen === true,
    offBlocksUtc: typeof row.offBlocksUtc === "string" ? row.offBlocksUtc : undefined,
    airborneUtc: typeof row.airborneUtc === "string" ? row.airborneUtc : undefined,
    landedUtc: typeof row.landedUtc === "string" ? row.landedUtc : undefined,
    onBlocksUtc: typeof row.onBlocksUtc === "string" ? row.onBlocksUtc : undefined,
    fuelAirborneLb: asNum(row.fuelAirborneLb) ?? undefined,
    fuelLandedLb: asNum(row.fuelLandedLb) ?? undefined,
    airFlowLb: asNum(row.airFlowLb) ?? undefined,
    closed: row.closed === true,
    lastAirborne: row.lastAirborne === true,
    lastEngineRunning: row.lastEngineRunning === true,
    lastApuRunning: row.lastApuRunning === true,
    fuelTrack: hydrateFuelTrack(row.fuelTrack),
    engineMsEach: hydrateHourList(row.engineMsEach),
    lastEngineOn: Array.isArray(row.lastEngineOn) ? row.lastEngineOn.map((flag) => flag === true) : undefined,
    engineCount: asNum(row.engineCount) != null ? clampEngineCount(row.engineCount) : undefined,
    combustionTrueSeen: row.combustionTrueSeen === true,
    careerBefore: hydrateCareerBefore(row.careerBefore),
  };
}

function hydrateFuelTrack(raw: unknown): FuelTrack {
  const base = emptyFuelTrack();
  if (!raw || typeof raw !== "object") return base;
  const row = raw as Record<string, unknown>;
  const seen = Array.isArray(row.engSeen) ? row.engSeen : [];
  const load = row.tankLoad;
  return {
    flowLb: asNum(row.flowLb) ?? 0,
    flowSeen: row.flowSeen === true,
    unlimited: row.unlimited === true,
    dump: row.dump === true,
    engSeen: [0, 1, 2, 3].map((index) => seen[index] === true) as [boolean, boolean, boolean, boolean],
    apuEverPublished: row.apuEverPublished === true,
    sawPower: row.sawPower === true,
    shutdownLatched: row.shutdownLatched === true,
    tankLoad:
      load === "state_saving" || load === "default_reload" || load === "no_snapshot" || load === "no_quantity"
        ? load
        : undefined,
  };
}

function hydrateShutdown(raw: unknown): FuelShutdownSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const registration = displayRegistration(typeof row.registration === "string" ? row.registration : "");
  const atUtc = typeof row.atUtc === "string" ? row.atUtc : "";
  const fuelQtyLb = asNum(row.fuelQtyLb);
  if (!registration || !atUtc || fuelQtyLb == null || fuelQtyLb < 0) return null;
  const tanks = Array.isArray(row.tanks)
    ? row.tanks.flatMap((tank) => {
        if (!tank || typeof tank !== "object") return [];
        const entry = tank as Record<string, unknown>;
        const id = typeof entry.id === "string" ? entry.id : "";
        const lb = asNum(entry.lb);
        if (!id || lb == null || lb < 0) return [];
        return [{ id, lb }];
      })
    : [];
  return { registration, atUtc, fuelQtyLb, tanks };
}

export function hydrateTailTelemetryState(raw?: unknown): TailTelemetryState {
  if (!raw || typeof raw !== "object") return { ...EMPTY_TAIL_TELEMETRY };
  const row = raw as Record<string, unknown>;
  const totals: Record<string, TailTelemetryTotals> = {};
  const rawTotals = row.totals && typeof row.totals === "object" ? (row.totals as Record<string, unknown>) : {};
  for (const [key, value] of Object.entries(rawTotals)) {
    const hit = hydrateTotals(value);
    if (hit) totals[normalizeTailKey(key) || normalizeTailKey(hit.registration)] = hit;
  }
  const trips = Array.isArray(row.trips)
    ? row.trips.map(hydrateTrip).filter((t): t is TailTripTelemetry => Boolean(t))
    : [];
  const shutdownByTail: Record<string, FuelShutdownSnapshot> = {};
  const rawShutdown =
    row.shutdownByTail && typeof row.shutdownByTail === "object" ? (row.shutdownByTail as Record<string, unknown>) : {};
  for (const [key, value] of Object.entries(rawShutdown)) {
    const snap = hydrateShutdown(value);
    if (snap) shutdownByTail[normalizeTailKey(key) || normalizeTailKey(snap.registration)] = snap;
  }
  return {
    session: hydrateSession(row.session),
    totals,
    trips: trips.length > TAIL_TRIP_TELEMETRY_MAX ? trips.slice(trips.length - TAIL_TRIP_TELEMETRY_MAX) : trips,
    shutdownByTail,
  };
}

export function liveSessionAsDraft(session: TailTelemetrySession | null): {
  airTimeHours: number | null;
  engineHours: number | null;
  engineMxHours: number | null;
  engineDetail: string | null;
  blockHours: number | null;
  apuHours: number | null;
  fuelUsedLb: number | null;
  engineSource: EngineTimeSource | null;
  apuReliable: boolean;
} | null {
  if (!session) return null;
  const air = hoursFromMs(session.airTimeMs);
  const eng = hoursFromMs(session.engineMs);
  const mx = engineMxFromSession(session);
  const blk = hoursFromMs(sessionBlockMs(session));
  const apu = session.apuReliable ? hoursFromMs(session.apuMs) : null;
  const measured = measuredBurn(session);
  const fuel = measured.reliable ? (fuelUsedFromSession(session) ?? null) : null;
  if (air <= 0 && eng <= 0 && fuel == null && mx.engineMxHours <= 0) return null;
  return {
    airTimeHours: air > 0 ? air : null,
    engineHours: eng > 0 ? eng : null,
    engineMxHours: mx.engineMxHours > 0 ? mx.engineMxHours : null,
    engineDetail: mx.engineDetail,
    blockHours: blk > 0 ? blk : null,
    apuHours: apu != null && apu > 0 ? apu : apu,
    fuelUsedLb: fuel,
    engineSource: session.engineSource,
    apuReliable: session.apuReliable,
  };
}
